/* eslint-disable import/no-deprecated */
import { useDebounce } from '@fullstory/react-hooks';
import { classNames } from '@fullstory/util/fmt';
import { Moment } from 'moment';
import React, { ComponentProps, ReactNode, useRef, useState } from 'react';
import { Popover } from '../../../../components/Popover';
import { Icon } from '../../../../deprecated/Icon';
import { Typography } from '../../../../deprecated/Typography';
import { Box } from '../../../Box';
import { Flex } from '../../../Flex';
import { RangeColor } from '../../types';
import css from './index.module.scss';

export interface DayLabelProps {
  /**
   * The day that will be rendered, must be a Moment object.
   */
  day: Moment;
  /**
   * Controls the selected and prominent color of the day.
   */
  color?: RangeColor;
  /**
   * If `true`, day will be rendered prominently. Useful for the start and end dates
   * of a selected range.
   */
  prominent?: boolean;
  /**
   * If `true`, day will be disabled and non-interactive. Useful for days that are
   * outside of the min/max dates or disabled for some other reason.
   */
  disabled?: boolean;
  /**
   * If provided, the day will be highlighted and an info tooltip will be displayed
   * on hover.
   */
  infoTooltip?: ReactNode;
}

const DayLabel: React.FC<DayLabelProps> = ({
  day,
  color = 'primary',
  prominent = false,
  disabled = false,
  infoTooltip,
}) => {
  const dayRef = useRef<HTMLDivElement>(null);
  const [hovered, setHovered] = useState(false);
  const hasInfoTooltip = Boolean(infoTooltip);
  let labelVariant: ComponentProps<typeof Typography>['variant'] = 'body3';
  let labelColor: ComponentProps<typeof Typography>['color'] = 'neutrals-n11';
  if (disabled) {
    labelColor = 'neutrals-n5';
  } else if (prominent || hasInfoTooltip) {
    labelVariant = 'heading5';
    labelColor = 'neutrals-n0';
  }
  // if day has an info tooltip, bind mouse event listeners for hovered state
  const infoTooltipProps = hasInfoTooltip
    ? {
        onMouseEnter: () => setHovered(true),
        onMouseLeave: () => setHovered(false),
      }
    : {};
  // debounce the hover state to prevent flickering when moving mouse into tooltip
  const hoveredDebounced = useDebounce(hovered, 150);
  return (
    <Box
      data-component="DayLabel"
      ref={dayRef}
      className={classNames(css.dayLabel, css[color], {
        [css.prominent]: prominent,
        [css.info]: hasInfoTooltip,
      })}
      {...infoTooltipProps}
    >
      <Typography variant={labelVariant} color={labelColor}>
        {day.format('D')}
      </Typography>
      {hasInfoTooltip && (
        <Popover open={hoveredDebounced} anchor={dayRef.current} placement="top" offset={8} arrow>
          <Box
            padding={12}
            onClick={evt => {
              // prevent clicks inside the popover from selecting the day
              evt.stopPropagation();
            }}
          >
            <Flex alignItems="flex-start">
              <Box flexShrink={0} marginRight={8}>
                <Icon name="error-warning-fill" color="accent-warning" />
              </Box>
              <Box maxWidth={250}>{infoTooltip}</Box>
            </Flex>
          </Box>
        </Popover>
      )}
    </Box>
  );
};

const MemoizedDayLabel = React.memo(DayLabel);

export { MemoizedDayLabel as DayLabel };
